import React, { useState, useEffect, useCallback } from "react";
import DataTable from "../DataTable";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import CachedOutlinedIcon from "@mui/icons-material/CachedOutlined";
import SendIcon from "@mui/icons-material/Send";
import ScheduleSendIcon from "@mui/icons-material/ScheduleSend";
import ArchiveIcon from '@mui/icons-material/Archive';
import DeleteIcon from "@mui/icons-material/Delete";
import FeedIcon from '@mui/icons-material/Feed';
import { handleRefreshClick, handleScheduleClick, handleDeleteClick, handleDateChange, handleScheduleSubmit, handleSingleArchiveClick } from "./Handlers";
import ScheduleDialog from "./DateTimePicker";
import DateFilterMenu from "./filters/DateFilterMenu";
import SourceFilterMenu from "./filters/SourceFilterMenu";
import CategoryFilterMenu from "./filters/CategoryFilterMenu";
import SentimentFilterMenu from "./filters/SentimentFilterMenu";
import PublishPostModal from './modals/PublishPostModal';
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import api_client from "../Authentication/axiosConfig";


const NewsTable = ({ 
  showArchiveButton = true, 
  onArchiveClick = handleSingleArchiveClick,
  initialNewsData = null,
  disablePagination = false,
  disableServerPagination = false,
  staticData = false
}) => {
  const [open, setOpen] = useState(false);
  const [isPublishPostModalOpen, setPublishPostModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedArticleData, setSelectedArticleData] = useState({ id: '', title: '', summary: '', llmSummary: '' });

  // Server-side filtering state
  const [filters, setFilters] = useState({});
  
  // Table data & pagination
  const [newsData, setNewsData] = useState(initialNewsData || []);
  const [isLoading, setIsLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(initialNewsData ? initialNewsData.length : 0);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 50,
    page: 0,
  });

  const fetchNewsData = useCallback(async (filterParams = filters) => {
    // If using static data mode, don't fetch from API
    if (isLoading || staticData) return;
    
    try {
      setIsLoading(true);
      const endpoint_path = showArchiveButton ? "/list_news/" : "/list_archived_news";
      
      // Build query parameters for the API request
      const params = {
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        ...filterParams  // Include filter parameters from state
      };
      
      const newsResponse = await api_client.get(`${endpoint_path}`, { params });
      
      if (newsResponse && newsResponse.status === 200) {
        setNewsData(newsResponse.data.results);
        setTotalRows(newsResponse.data.count);
      } else {
        setNewsData([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching news: ", error);
      toast.error(`Error fetching news: ${error.message}`);
      setNewsData([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [paginationModel, filters, showArchiveButton]);

  // Fetch data when pagination or filters change (only if not static)
  useEffect(() => {
    if (!staticData) {
      fetchNewsData();
    }
  }, [paginationModel, fetchNewsData, staticData]);

  const handleOpenPublishPostModal = (articleId, articleTitle, articleSummary, articleLLMSummary) => {
    setSelectedArticleData({ 
      id: articleId, 
      title: articleTitle, 
      summary: articleSummary,
      llmSummary: articleLLMSummary 
    });
    setPublishPostModalOpen(true);
  };

  const handleClosePublishPostModal = () => {
    setPublishPostModalOpen(false);
    setSelectedArticleData({ id: '', title: '', summary: '' });
  };

  // Function to fetch news directly from AddNews component
  const [isFetchingNews, setIsFetchingNews] = useState(false);
  
  const fetchNews = async () => {
    if (isFetchingNews) return;
    
    setIsFetchingNews(true);
    try {
      const response = await api_client.get(`/add_news/`);
      
      if (response && (response.status === 200 || response.status === 202)) {
        toast.success('Request to update news successfully received. The dashboard will update shortly.');
        // Refresh the news list after a short delay to allow backend processing
        setTimeout(() => {
          fetchNewsData();
        }, 3000);
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      toast.error(`Error fetching news: ${error.message || 'Unknown error'}`);
    } finally {
      setIsFetchingNews(false);
    }
  };

  // Handle pagination model change
  const handlePaginationModelChange = (newModel) => {
    setPaginationModel(newModel);
  };

  const columns = [
    {
      field: "sentiment",
      headerName: "Sentiment",
      width: 100,
      align: "center",
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      renderCell: (params) => (
        <a
          href={params.row.link}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 underline hover:text-blue-700"
        >
          {params.value}
        </a>
      ),
    },
    { field: "published", headerName: "Published", width: 100 },
    {
      field: "source",
      headerName: "Source",
    },
    { field: "category", headerName: "Category", width: 110, align: "center" },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      renderCell: (params) => (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Tooltip title="Publish article">
            <IconButton
              onClick={() => handleOpenPublishPostModal(params.row.id, params.row.title, params.row.summary, params.row.llmSummary)}
              style={{ flex: 1 }}
            >
              <SendIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Schedule article">
            <IconButton
              onClick={() => handleScheduleClick(
                { id: params.row.id, title: params.row.title, summary: params.row.summary },
                setSelectedArticleData,
                setOpen
              )}
              style={{ flex: 1 }}
            >
              <ScheduleSendIcon />
            </IconButton>
          </Tooltip>
          {showArchiveButton && (
            <Tooltip title="Archive article">
              <IconButton
                onClick={() => onArchiveClick(params.row.id, fetchNewsData)}
                style={{ flex: 1 }}
              >
                <ArchiveIcon />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="Delete article">
            <IconButton
              onClick={() => handleDeleteClick(params.row.id, fetchNewsData)}
              style={{ flex: 1 }}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  const rows = newsData.map((item) => ({
    id: item.id,
    sentiment: item.sentiment,
    published: item.published,
    source: item.source,
    category: item.category,
    title: item.title,
    summary: item.summary,
    llmSummary: item.llm_summary,
    link: item.link,
    scheduled: item.scheduled_time,
  }));

  return (
    <div style={{
      width: "90%", 
      margin: "0 auto", 
      height: staticData ? "auto" : "90vh", 
      overflowY: "auto"
    }}>
      {!staticData && (
        <div style={{display: "flex", justifyContent: "flex-end", alignItems: "center", marginRight: "8px"}}>
        <Tooltip 
          title={filters.time_filter ? `Date filter: ${filters.time_filter}` : "Filter by date"}
          enterDelay={100}
          leaveDelay={200}
          PopperProps={{
            disablePortal: true,
            popperOptions: {
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    altAxis: true,
                    tether: false,
                    padding: 8,
                  },
                },
              ],
            },
          }}
        >
          <span>
            <DateFilterMenu
              filters={filters} 
              setFilters={setFilters} 
              fetchData={fetchNewsData}
            />
          </span>
        </Tooltip>
        
        <Tooltip 
          title={filters.sentiment ? `Sentiment filter: ${filters.sentiment}` : "Filter by sentiment"}
          enterDelay={100}
          leaveDelay={200}
          PopperProps={{
            disablePortal: true,
            popperOptions: {
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    altAxis: true,
                    tether: false,
                    padding: 8,
                  },
                },
              ],
            },
          }}
        >
          <span>
            <SentimentFilterMenu
              filters={filters}
              setFilters={setFilters}
              fetchData={fetchNewsData}
            />
          </span>
        </Tooltip>
        
        <Tooltip 
          title={filters.source ? `Source filter: ${filters.source}` : "Filter by source"}
          enterDelay={100}
          leaveDelay={200}
          PopperProps={{
            disablePortal: true,
            popperOptions: {
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    altAxis: true,
                    tether: false,
                    padding: 8,
                  },
                },
              ],
            },
          }}
        >
          <span>
            <SourceFilterMenu
              filters={filters}
              setFilters={setFilters}
              fetchData={fetchNewsData}
            />
          </span>
        </Tooltip>
        
        <Tooltip 
          title={filters.category ? `Category filter: ${filters.category}` : "Filter by category"}
          enterDelay={100}
          leaveDelay={200}
          PopperProps={{
            disablePortal: true,
            popperOptions: {
              modifiers: [
                {
                  name: "preventOverflow",
                  options: {
                    altAxis: true,
                    tether: false,
                    padding: 8,
                  },
                },
              ],
            },
          }}
        >
          <span>
            <CategoryFilterMenu
              filters={filters}
              setFilters={setFilters}
              fetchData={fetchNewsData}
            />
          </span>
        </Tooltip>
        
        <Tooltip 
          title="Refresh news list"
          enterDelay={100}
          leaveDelay={200}
        >
          <IconButton
            onClick={() => handleRefreshClick(fetchNewsData)}
            sx={{
              fontWeight: "bold",
              fontSize: "large",
            }}
          >
            <CachedOutlinedIcon/>
          </IconButton>
        </Tooltip>
        
        <Tooltip 
          title="Fetch new articles"
          enterDelay={100}
          leaveDelay={200}
        >
          <IconButton
            onClick={fetchNews}
            disabled={isFetchingNews}
            sx={{
              fontWeight: "bold",
              fontSize: "large",
              color: isFetchingNews ? 'grey' : undefined
            }}
          >
            <FeedIcon />
          </IconButton>
        </Tooltip>
      </div>
      )}
      <div>
        <DataTable
          rows={rows}
          columns={columns}
          loading={isLoading}
          paginationModel={paginationModel}
          setPaginationModel={handlePaginationModelChange}
          totalRows={totalRows}
          disablePagination={disablePagination}
          disableServerPagination={disableServerPagination}
        />
      </div>
      <PublishPostModal
        isOpen={isPublishPostModalOpen}
        onClose={handleClosePublishPostModal}
        selectedArticleId={selectedArticleData.id}
        selectedArticleTitle={selectedArticleData.title}
        selectedArticleSummary={selectedArticleData.summary}
        selectedArticleLLMSummary={selectedArticleData.llmSummary}
      />
      <ScheduleDialog
        open={open}
        selectedDate={selectedDate}
        onDateChange={(date) => handleDateChange(date, setSelectedDate)}
        onCancel={() => setOpen(false)}
        onSubmit={() => handleScheduleSubmit(selectedArticleData, selectedDate, setOpen, fetchNewsData)}
      />
    </div>
  );
};

export default NewsTable;
